import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ChartNode } from '../../models/chart-node.model';
import { OrgChartDragService } from '../../services/org-chart-drag.service';

export type ChartNodeAction = 'details' | 'focus' | 'addPosition' | 'addEmployee' | 'addOrganization' | 'edit' | 'delete';

export interface ChartNodeActionEvent {
  action: ChartNodeAction;
  node: ChartNode;
}

const AVATAR_STRIP_SIZE = 5;
// In exports, long employee lists are split into columns of about this many rows (up to 4 columns)
const EXPORT_EMPLOYEES_PER_COLUMN = 10;
const MAX_EXPORT_EMPLOYEE_COLUMNS = 4;

@Component({
  selector: 'app-org-chart-node-card',
  templateUrl: './org-chart-node-card.component.html',
  styleUrls: ['./org-chart-node-card.component.scss'],
})
export class OrgChartNodeCardComponent {
  @Input() node: ChartNode;
  @Input() editable = false;
  @Input() highlightedUid: string | null = null;
  // Set during paged exports when this node has its own section, e.g. "page 4".
  @Input() continuedOn: string | null = null;

  @Output() action = new EventEmitter<ChartNodeActionEvent>();
  @Output() toggle = new EventEmitter<ChartNode>();

  dropHover: ChartNode | null = null;
  private failedPhotos = new Set<string>();

  constructor(public drag: OrgChartDragService) {
  }

  get stripEmployees(): ChartNode[] {
    return this.node.employees.slice(0, AVATAR_STRIP_SIZE);
  }

  get hiddenEmployeeCount(): number {
    return Math.max(0, this.node.employees.length - AVATAR_STRIP_SIZE);
  }

  get childCount(): number {
    return this.node.childCount ?? this.node.children.length;
  }

  get exportEmployeeColumns(): number {
    return Math.min(MAX_EXPORT_EMPLOYEE_COLUMNS, Math.ceil(this.node.employees.length / EXPORT_EMPLOYEES_PER_COLUMN));
  }

  get exportEmployeeRows(): number {
    return Math.ceil(this.node.employees.length / this.exportEmployeeColumns);
  }

  get typeIcon(): string {
    return this.node.type === 'organization' ? 'corporate_fare' : 'work';
  }

  get occupancyLabel(): string {
    const count = this.node.employees.length;
    return this.node.slots ? `${count} / ${this.node.slots}` : `${count}`;
  }

  emit(action: ChartNodeAction, node: ChartNode = this.node) {
    this.action.emit({ action, node });
  }

  toggleEmployees() {
    this.node.employeesExpanded = !this.node.employeesExpanded;
  }

  // Photos

  hasPhoto(employee: ChartNode): boolean {
    return !!employee.photo && !this.failedPhotos.has(employee.uid);
  }

  photoFailed(employee: ChartNode) {
    this.failedPhotos.add(employee.uid);
  }

  initials(employee: ChartNode): string {
    return employee.name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('');
  }

  // Drag & drop

  onDragStart(event: DragEvent, node: ChartNode) {
    if (!this.editable) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', node.uid);
    this.drag.start(node);
  }

  onDragEnd() {
    this.drag.end();
    this.dropHover = null;
  }

  onDragOver(event: DragEvent) {
    if (this.drag.canDropOn(this.node)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      this.dropHover = this.node;
    }
  }

  onDragLeave(event: DragEvent) {
    const related = event.relatedTarget as Node | null;
    if (!related || !(event.currentTarget as HTMLElement).contains(related)) {
      this.dropHover = null;
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dropHover = null;
    this.drag.drop(this.node);
  }
}
